import { useState } from "react";
import moment from "moment";
import { ApiCall } from "../../../helper/axios";

const MAX_CONCURRENT = 5;
let activeCount = 0;
const queue = [];
const inFlight = new Map();

const runNext = () => {
  if (activeCount >= MAX_CONCURRENT) return;
  if (queue.length === 0) return;

  const { fn, resolve, reject } = queue.shift();
  activeCount++;

  fn()
    .then(resolve)
    .catch(reject)
    .finally(() => {
      activeCount--;
      runNext();
    });
};

const queueRequest = (fn) => {
  return new Promise((resolve, reject) => {
    queue.push({ fn, resolve, reject });
    runNext();
  });
};

export const useHotelReview = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [data, setData] = useState([]);
  const [avg, setAvg] = useState(0);
  const [total, setTotal] = useState(0);
  const [grouped, setGrouped] = useState([]);
  const [additionalData, setAdditionalData] = useState({});

  /**
   * Refresh hotel reviews
   * @param {string | number} id - Hotel ID (used as cache key)
   * @param {string} hotelName - Hotel name (for API)
   * @param {string} hotelVendor - Hotel vendor
   * @param {object} additionalHotelData - Extra data for API
   */
  const refresh = async (id, hotelName, hotelVendor, additionalHotelData = {}, hotel) => {
    const cacheKey = `hotel_${hotelVendor}_${id}`;
    const now = moment().unix();

    try {
      setLoading(true);
      const saved = sessionStorage.getItem(cacheKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        const { reviews, details } = parsed;
        applyData(reviews, details);
        return;
      }

      let resultPromise = inFlight.get(cacheKey);
      if (!resultPromise) {
        let payload = {};
        if (hotelVendor === "HOTELRACK") {
          payload = {
            hotelName,
            id,
            hotelVendor,
            ...additionalHotelData
          };
        } else if (hotelVendor === "RATEHAWK") {
          payload = {
            hid: hotel?.hid,
            vendor: "RATEHAWK",
            hotelName
          };
        }
        resultPromise = queueRequest(() =>
          ApiCall("POST", "/hotels/reviews-and-photos", payload)
        );
        inFlight.set(cacheKey, resultPromise);
        resultPromise.finally(() => inFlight.delete(cacheKey));
      }

      const response = await resultPromise;

      if (response?.status === 200 && response?.data?.statusCode === 200) {
        const data = response.data.data;
        const reviews = data?.saved;
        const details = data.details ?? {};
        applyData(reviews, details);
        sessionStorage.setItem(
          cacheKey,
          JSON.stringify({
            timestamp: now,
            reviews,
            details
          })
        );
      }
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };


  /**
   * Apply reviews data to state (cleanly, no mutations)
   * @param avg
   * @param total
   */
  const generateRatingDistribution = (avg, total) => {
    if (!total || total <= 0) {
      return [
        { group: 5, count: 0, percentage: 0 },
        { group: 4, count: 0, percentage: 0 },
        { group: 3, count: 0, percentage: 0 },
        { group: 2, count: 0, percentage: 0 },
        { group: 1, count: 0, percentage: 0 },
      ];
    }

    const groups = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

    // Normalize avg (0–1)
    const x = avg / 5;

    // Google-like skew (important)
    let weights = {
      5: Math.pow(x, 1) * 6,
      4: Math.pow(x, 1.7) * 3,
      3: Math.pow(x, 2.5) * 1.2,
      2: Math.pow(x, 3.5) * 0.6,
      1: Math.pow(x, 5) * 0.3,
    };

    // Normalize
    const totalWeight = Object.values(weights).reduce((a, b) => a + b, 0);

    Object.keys(weights).forEach((k) => {
      weights[k] = weights[k] / totalWeight;
    });

    // Convert to counts
    let remaining = total;

    Object.keys(weights).forEach((star) => {
      const count = Math.round(weights[star] * total);
      groups[star] = count;
      remaining -= count;
    });

    // Fix rounding
    while (remaining !== 0) {
      for (let i = 5; i >= 1 && remaining !== 0; i--) {
        if (remaining > 0) {
          groups[i]++;
          remaining--;
        } else if (groups[i] > 0) {
          groups[i]--;
          remaining++;
        }
      }
    }

    // Final format
    return Object.keys(groups)
      .sort((a, b) => b - a)
      .map((g) => ({
        group: Number(g),
        count: groups[g],
        percentage: (groups[g] * 100) / total,
      }));
  };

  const applyData = (reviews, details) => {
    setData(reviews);
    setAdditionalData(details);

    const totalRatings = parseInt(reviews?.[0]?.userRatingCount ?? "0");
    setTotal(totalRatings);

    const avgRating = parseFloat(reviews?.[0]?.rating ?? 0);
    setAvg(avgRating);

    const groupedData = generateRatingDistribution(
      avgRating,
      totalRatings
    );

    setGrouped(groupedData);
  };

  return {
    loading,
    data,
    error,
    refresh,
    avg: avg.toFixed(1),
    total,
    grouped,
    additionalData,
  };
};
